import { Button, Dialog } from '@portal/components'
import React, { ComponentProps } from 'react'

type Props = {
  /** Whether the modal is open */
  open: boolean
  /** The title of the modal */
  title: string
  /** The description of the modal */
  description?: React.ReactNode
  /** The optional variant of the modal */
  variant?: 'default' | 'warning'
  /** The content of the modal */
  children?: React.ReactNode
  /** The text of the close button */
  cancelButtonText?: string
  /** The text of the confirm button */
  confirmButtonText?: string
  /** Whether the close icon is rendered */
  showCloseIcon?: boolean
  /** Whether the confirm button is rendered */
  showConfirmButton?: boolean
  /** Whether the cancel button is rendered */
  showCancelButton?: boolean
  /** Indicates wheather the confirm buttom should be disabled. */
  confirmButtonDisabled?: boolean
  /** The form id of the confirmation button. If present, the type of the button will be submit. */
  confirmationButtonForm?: string
  /** Indicates wheather the close buttom should be disabled. */
  cancelButtonDisabled?: boolean
  /** Callback for when the confirm button is clicked */
  onConfirm?: () => void
  /** Callback for when the cancel button is clicked */
  onCancel?: () => void
} & Pick<ComponentProps<typeof Dialog.Content>, 'size'>

export default function ConfirmationModal({
  open,
  title,
  size = 'md',
  description,
  variant,
  children,
  cancelButtonText,
  confirmButtonText,
  showCloseIcon,
  showConfirmButton = true,
  showCancelButton = true,
  confirmButtonDisabled,
  confirmationButtonForm,
  cancelButtonDisabled,
  onConfirm,
  onCancel,
  ...rest
}: Props) {
  const buttonVariant = variant === 'warning' ? 'critical' : 'primary'

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <Dialog.Content
        size={size}
        showClose={showCloseIcon}
        aria-describedby={undefined}
        {...rest}
      >
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          {description && (
            <Dialog.Description>{description}</Dialog.Description>
          )}
        </Dialog.Header>
        {children}
        <Dialog.Footer>
          {showCancelButton && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={cancelButtonDisabled}
              data-testid="confirmationModalCancelButton"
            >
              {cancelButtonText || 'Cancel'}
            </Button>
          )}
          {showConfirmButton && (
            <Button
              variant={buttonVariant}
              onClick={onConfirm}
              disabled={confirmButtonDisabled}
              form={confirmationButtonForm}
              type={confirmationButtonForm ? 'submit' : 'button'}
              data-testid="confirmationModalConfirmButton"
            >
              {confirmButtonText || 'Confirm'}
            </Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
